import { GetQuickFilterTextParams, ICellRendererParams, IRowNode, ValueGetterParams } from '@ag-grid-community/core';
import { isNil } from "lodash-es";

import { BodyXS, Box, Status } from "@parspec/pixel";

import { ColorCode, DOCUMENT_STATUS_COLOR_MAP, DocumentStatusEnum, PROJECT_STAGE_OPTIONS_MAP } from "src/features/shared/constants";
import { CustomTableHeader } from "src/features/shared/CustomTableHeader";
import { StatusTemplate, TableLinkTemplate } from "src/features/shared/utils/utils";
import { customerComparator, dateComparator, getLastModifiedFilterText, perValueFormatter, stringComparator } from "./utils";
import { getCellAmountFormatter, getCellPercentFormatter, getFormattedLeadTimeRange } from "src/features/BOM/shared/utils";
import { PricingHistoryTableDataType } from "./types";
import { Customer } from "./Customer";
import { GetSourceTemplate } from "./components";
import { PROJECT_SOURCE_OPTIONS_MAP } from '../constant';

export const sourceCol = {
    headerName: 'Source',
    field: 'source',
    suppressMenu: false,
    filter: 'agSetColumnFilter',
    headerComponent: CustomTableHeader,
    headerComponentParams: {
        enableFiltering: true
    },
    width: 130,
    minWidth: 120,
    colId: 'source',
    cellRenderer: (params: ICellRendererParams) => {
        return (
            <Box display="flex" alignItems="center" height="100%">
                <GetSourceTemplate source={params.data.source} />
            </Box>
        );
    },
    getQuickFilterText: (params: GetQuickFilterTextParams) => PROJECT_SOURCE_OPTIONS_MAP[params.data.source]
};

export const getDefaultVisibleColDef = [
    sourceCol,
    { headerName: 'Edited On', field: 'editedOn', sortable: true, width: 116, minWidth: 116, comparator: dateComparator },
    {
        headerName: 'Edited By',
        field: 'editedBy',
        suppressMenu: false,
        filter: 'agSetColumnFilter',
        width: 100,
        minWidth: 100,
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableFiltering: true
        }
    },
    { headerName: 'Qty', field: 'qty', sortable: true, width: 80, minWidth: 80 },
    { headerName: 'UOM', field: 'uom', sortable: true, width: 100, minWidth: 100 },
    { headerName: 'Manufacturer', field: 'manufacturer', sortable: true, width: 176, minWidth: 176 },
    {
        headerName: 'Model Number',
        field: 'modelNumber',
        sortable: true,
        width: 200,
        minWidth: 200
    },
    { headerName: 'Description', field: 'description', width: 188, minWidth: 188 },
    {
        headerName: 'Cost',
        field: 'cost',
        sortable: true,
        width: 100,
        minWidth: 100,
        valueFormatter: getCellAmountFormatter,
        getQuickFilterText: getCellAmountFormatter
    },
    {
        headerName: 'Per',
        field: 'per',
        sortable: true,
        width: 100,
        minWidth: 100,
        valueFormatter: perValueFormatter
    },
    {
        headerName: 'Discount (%)',
        field: 'discount',
        sortable: true,
        width: 92,
        minWidth: 92,
        headerComponent: CustomTableHeader,
        valueFormatter: getCellPercentFormatter,
        getQuickFilterText: getCellPercentFormatter
    },
    {
        headerName: 'Discounted Cost',
        field: 'discountedCost',
        sortable: true,
        width: 104,
        minWidth: 104,
        headerComponent: CustomTableHeader,
        valueFormatter: getCellAmountFormatter,
        getQuickFilterText: getCellAmountFormatter
    },
    {
        headerName: 'Margin (%)',
        field: 'margin',
        sortable: true,
        width: 84,
        minWidth: 84,
        headerComponent: CustomTableHeader,
        valueFormatter: getCellPercentFormatter,
        getQuickFilterText: getCellPercentFormatter
    },
    {
        headerName: 'Sell Price',
        field: 'sellPrice',
        sortable: true,
        width: 100,
        minWidth: 100,
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableSorting: true
        },
        valueFormatter: getCellAmountFormatter,
        getQuickFilterText: getCellAmountFormatter
    },
    {
        headerName: 'Lead time Value',
        colId: 'leadTimeValue',
        field: 'leadTimeValue',
        sortable: true,
        width: 152,
        minWidth: 152,
        valueGetter: (params: ValueGetterParams<PricingHistoryTableDataType>) => {
            const { data } = params;
            if (isNil(data)) return null;
            const range = getFormattedLeadTimeRange(data);
            return range || null;
        },
        comparator: (
            _valueA: ICellRendererParams,
            _valueB: ICellRendererParams,
            nodeA: IRowNode<PricingHistoryTableDataType>,
            nodeB: IRowNode<PricingHistoryTableDataType>
        ) => {
            const { data: nodeAData } = nodeA;
            const { data: nodeBData } = nodeB;

            const diff = (nodeAData?.leadTimeMin || 0) - (nodeBData?.leadTimeMin || 0);
            if (!diff) {
                return (nodeAData?.leadTimeMax || 0) - (nodeBData?.leadTimeMax || 0);
            }
            return diff;
        },
    },
    { headerName: 'Lead time Unit', field: 'leadTimeUnit', sortable: true, width: 152, minWidth: 152 },
    {
        headerName: 'Project',
        field: 'projectName',
        width: 180,
        minWidth: 180,
        cellRenderer: (params: ICellRendererParams) => params.data.projectId ? <TableLinkTemplate to={`/v2/project/${params.data.projectId}`} label={params.data.projectName} target="_blank" /> : params.data.projectName,
        getQuickFilterText: (params: GetQuickFilterTextParams) => params.data.projectName
    },
    {
        headerName: 'Project Stage',
        field: 'projectStage',
        suppressMenu: false,
        filter: 'agSetColumnFilter',
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableFiltering: true
        },
        width: 156,
        minWidth: 156,
        cellRenderer: (params: ICellRendererParams) => <StatusTemplate stage={params?.data?.projectStage} />,
        getQuickFilterText: (params: GetQuickFilterTextParams) => PROJECT_STAGE_OPTIONS_MAP[params.data.projectStage || '']
    },
    {
        headerName: 'Branch Location',
        field: 'branchLocation',
        suppressMenu: false,
        filter: 'agSetColumnFilter',
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableFiltering: true
        },
        width: 120,
        minWidth: 120
    },
    {
        headerName: 'Quote Owner',
        field: 'quoteOwner',
        suppressMenu: false,
        filter: 'agSetColumnFilter',
        headerComponent: CustomTableHeader,
        comparator: (valueA: string, valueB: string) => stringComparator(valueA, valueB),
        headerComponentParams: {
            enableSorting: true,
            enableFiltering: true
        },
        width: 136,
        minWidth: 136
    },
    {
        headerName: 'BOM',
        field: 'bomName',
        width: 140,
        minWidth: 140,
        cellRenderer: (params: ICellRendererParams) => params.data.bomId ? <TableLinkTemplate to={`/v2/project/${params?.data?.projectId}/bom/${params.data.bomId}?tab=specification`} label={params.data.bomName} target="_blank" /> : '-',
        getQuickFilterText: (params: GetQuickFilterTextParams) => params.data.bomName
    },
    {
        headerName: 'Primary Customer',
        field: 'primaryCustomerName',
        width: 140,
        minWidth: 140,
        sortable: true,
        filter: 'agSetColumnFilter',
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableFiltering: true,
            enableSorting: true
        },
        suppressMenu: false,
        cellRenderer: (params: ICellRendererParams) => <Customer {...params?.data?.primaryCustomer} />,
        getQuickFilterText: (params: GetQuickFilterTextParams) => getLastModifiedFilterText(params, 'primaryCustomer'),
        comparator: (valueA: string, valueB: string, nodeA: IRowNode, nodeB: IRowNode, isDescending: boolean) => customerComparator(valueA, valueB, nodeA, nodeB, isDescending, 'primaryCustomer'),
        valueGetter: (params: ValueGetterParams) => params?.data?.primaryCustomer?.company_name,
        cellClass: 'grid-cell-content-centered'
    },
    {
        headerName: 'Secondary Customer',
        field: 'secondaryCustomerName',
        width: 140,
        minWidth: 140,
        sortable: true,
        filter: 'agSetColumnFilter',
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableFiltering: true,
            enableSorting: true
        },
        suppressMenu: false,
        cellRenderer: (params: ICellRendererParams) => <Customer {...params?.data?.secondaryCustomer} />,
        getQuickFilterText: (params: GetQuickFilterTextParams) => getLastModifiedFilterText(params, 'secondaryCustomer'),
        comparator: (valueA: string, valueB: string, nodeA: IRowNode, nodeB: IRowNode, isDescending: boolean) => customerComparator(valueA, valueB, nodeA, nodeB, isDescending, 'secondaryCustomer'),
        valueGetter: (params: ValueGetterParams) => params?.data?.secondaryCustomer?.company_name
    },
    {
        headerName: 'Quote Status',
        field: 'quoteStatus',
        width: 120,
        minWidth: 120,
        suppressMenu: false,
        filter: 'agSetColumnFilter',
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableFiltering: true
        },
        valueGetter: (params: ValueGetterParams) => params.data.quoteStatus || DocumentStatusEnum.PENDING,
        cellRenderer: (params: ICellRendererParams) => {
            const status = params.value;

            const color = DOCUMENT_STATUS_COLOR_MAP[status as keyof typeof DOCUMENT_STATUS_COLOR_MAP] as ColorCode;
            return (
                <Box display={'flex'} alignItems="center" height="100%">
                    <Status color={color}>
                        <BodyXS color={color}>{status}</BodyXS>
                    </Status>
                </Box>
            );
        }
    },
    {
        headerName: 'Outside Sales',
        field: 'salesAgent',
        width: 170,
        minWidth: 150,
        suppressMenu: false,
        filter: 'agSetColumnFilter',
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableFiltering: true
        }
    }
];

export const getAllColDef = [
    ...getDefaultVisibleColDef,
    {
        headerName: 'Date Uploaded',
        field: 'dateUploaded',
        width: 116,
        minWidth: 116,
        headerComponent: CustomTableHeader,
        suppressMenu: false,
        sortable: true,
        comparator: dateComparator,
    },
    {
        headerName: 'Product ERP Code',
        field: 'productErpCode',
        width: 120,
        minWidth: 120,
        filter: 'agSetColumnFilter',
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableFiltering: true
        }
    },
    {
        headerName: 'Quote ERP ID',
        field: 'quoteErpId',
        width: 120,
        minWidth: 120,
        filter: 'agSetColumnFilter',
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableFiltering: true
        }
    },
    { headerName: 'Quote Due Date', field: 'quoteDueDate', sortable: true, width: 116, minWidth: 116, comparator: dateComparator, headerComponent: CustomTableHeader, },
    { headerName: 'Quote Creation Date', field: 'quoteCreationDate', sortable: true, width: 116, minWidth: 116, comparator: dateComparator, headerComponent: CustomTableHeader, },
    {
        headerName: 'Quote Creator',
        field: 'quoteCreator',
        width: 120,
        minWidth: 120,
        filter: 'agSetColumnFilter',
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableFiltering: true
        }
    },
    {
        headerName: 'Source Quote Number',
        field: 'sourceQuoteNumber',
        width: 120,
        minWidth: 120,
        filter: 'agSetColumnFilter',
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableFiltering: true
        }
    },
    {
        headerName: 'Stakeholder 1',
        field: 'stakeHolder1',
        width: 140,
        minWidth: 140,
        sortable: true,
        filter: 'agSetColumnFilter',
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableFiltering: true,
            enableSorting: true
        },
        suppressMenu: false,
        cellRenderer: (params: ICellRendererParams) => <Customer {...params?.data?.stakeHolder1} />,
        getQuickFilterText: (params: GetQuickFilterTextParams) => getLastModifiedFilterText(params, 'stakeHolder1'),
        comparator: (valueA: string, valueB: string, nodeA: IRowNode, nodeB: IRowNode, isDescending: boolean) => customerComparator(valueA, valueB, nodeA, nodeB, isDescending, 'stakeHolder1'),
        valueGetter: (params: ValueGetterParams) => params?.data?.stakeHolder1?.company_name,
        cellClass: 'grid-cell-content-centered'
    },
    {
        headerName: 'Stakeholder 2',
        field: 'stakeHolder2',
        width: 140,
        minWidth: 140,
        sortable: true,
        filter: 'agSetColumnFilter',
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableFiltering: true,
            enableSorting: true
        },
        suppressMenu: false,
        cellRenderer: (params: ICellRendererParams) => <Customer {...params?.data?.stakeHolder2} />,
        getQuickFilterText: (params: GetQuickFilterTextParams) => getLastModifiedFilterText(params, 'stakeHolder2'),
        comparator: (valueA: string, valueB: string, nodeA: IRowNode, nodeB: IRowNode, isDescending: boolean) => customerComparator(valueA, valueB, nodeA, nodeB, isDescending, 'stakeHolder2'),
        valueGetter: (params: ValueGetterParams) => params?.data?.stakeHolder2?.company_name,
        cellClass: 'grid-cell-content-centered'
    },
    {
        headerName: 'Stakeholder 3',
        field: 'stakeHolder3',
        width: 140,
        minWidth: 140,
        sortable: true,
        filter: 'agSetColumnFilter',
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableFiltering: true,
            enableSorting: true
        },
        suppressMenu: false,
        cellRenderer: (params: ICellRendererParams) => <Customer {...params?.data?.stakeHolder3} />,
        getQuickFilterText: (params: GetQuickFilterTextParams) => getLastModifiedFilterText(params, 'stakeHolder3'),
        comparator: (valueA: string, valueB: string, nodeA: IRowNode, nodeB: IRowNode, isDescending: boolean) => customerComparator(valueA, valueB, nodeA, nodeB, isDescending, 'stakeHolder3'),
        valueGetter: (params: ValueGetterParams) => params?.data?.stakeHolder3?.company_name,
        cellClass: 'grid-cell-content-centered'
    },
    {
        headerName: 'Stakeholder 4',
        field: 'stakeHolder4',
        width: 140,
        minWidth: 140,
        sortable: true,
        filter: 'agSetColumnFilter',
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableFiltering: true,
            enableSorting: true
        },
        suppressMenu: false,
        cellRenderer: (params: ICellRendererParams) => <Customer {...params?.data?.stakeHolder4} />,
        getQuickFilterText: (params: GetQuickFilterTextParams) => getLastModifiedFilterText(params, 'stakeHolder4'),
        comparator: (valueA: string, valueB: string, nodeA: IRowNode, nodeB: IRowNode, isDescending: boolean) => customerComparator(valueA, valueB, nodeA, nodeB, isDescending, 'stakeHolder4'),
        valueGetter: (params: ValueGetterParams) => params?.data?.stakeHolder4?.company_name,
        cellClass: 'grid-cell-content-centered'
    },
    {
        headerName: 'Stakeholder 5',
        field: 'stakeHolder5',
        width: 140,
        minWidth: 140,
        sortable: true,
        filter: 'agSetColumnFilter',
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableFiltering: true,
            enableSorting: true
        },
        suppressMenu: false,
        cellRenderer: (params: ICellRendererParams) => <Customer {...params?.data?.stakeHolder5} />,
        getQuickFilterText: (params: GetQuickFilterTextParams) => getLastModifiedFilterText(params, 'stakeHolder5'),
        comparator: (valueA: string, valueB: string, nodeA: IRowNode, nodeB: IRowNode, isDescending: boolean) => customerComparator(valueA, valueB, nodeA, nodeB, isDescending, 'stakeHolder5'),
        valueGetter: (params: ValueGetterParams) => params?.data?.stakeHolder5?.company_name,
        cellClass: 'grid-cell-content-centered'
    },
    {
        headerName: 'Project ERP ID',
        field: 'projectErpId',
        width: 120,
        minWidth: 120,
        filter: 'agSetColumnFilter',
        headerComponent: CustomTableHeader,
        headerComponentParams: {
            enableFiltering: true
        }
    },
    {
        headerName: 'Market Vertical',
        field: 'projectMarketVertical',
        width: 120,
        minWidth: 120,
        filter: 'agSetColumnFilter',
    },
    {
        headerName: 'Construction Scope',
        field: 'projectConstructionScope',
        width: 150,
        minWidth: 150,
        filter: 'agSetColumnFilter',
    },
    {
        headerName: 'Square feet(sqft)',
        field: 'projectFootage',
        width: 150,
        minWidth: 150,
        filter: 'agSetColumnFilter',
    },
    {
        headerName: 'Address Line 1',
        field: 'addressLine1',
        width: 120,
        minWidth: 120,
        filter: 'agSetColumnFilter',
    },
    {
        headerName: 'Address Line 2',
        field: 'addressLine2',
        width: 120,
        minWidth: 120,
        filter: 'agSetColumnFilter',
    },
    {
        headerName: 'Country',
        field: 'projectCountry',
        width: 120,
        minWidth: 120,
        filter: 'agSetColumnFilter',
    },
    {
        headerName: 'State',
        field: 'projectState',
        width: 120,
        minWidth: 120,
        filter: 'agSetColumnFilter',
    },
    {
        headerName: 'City',
        field: 'projectCity',
        width: 120,
        minWidth: 120,
        filter: 'agSetColumnFilter',
    },
    {
        headerName: 'Zip Code',
        field: 'projectZip',
        width: 120,
        minWidth: 120,
        filter: 'agSetColumnFilter',
    },
];